import { useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../../lib/api";

type Tab = "note" | "article" | "transcript" | "pdf";

const TABS: { id: Tab; label: string }[] = [
  { id: "note", label: "Notiz" },
  { id: "article", label: "Artikel-URL" },
  { id: "transcript", label: "Transkript" },
  { id: "pdf", label: "PDF" }
];

function useImportResult() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function run(fn: () => Promise<{ sourceId: string }>) {
    setError(null);
    setSubmitting(true);
    try {
      const { sourceId } = await fn();
      void navigate(`/library/${sourceId}`);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === "duplicate_source") {
        const existingId = err.details?.existingSourceId as string | undefined;
        setError(existingId ? `Bereits importiert: ${existingId}` : "Diese Quelle wurde bereits importiert.");
      } else {
        setError(err instanceof ApiRequestError ? err.message : "Import fehlgeschlagen.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return { error, submitting, run };
}

function TextField({ label, value, onChange, required, type = "text" }: { label: string; value: string; onChange: (v: string) => void; required?: boolean; type?: string }) {
  return (
    <div className="pw-field">
      <label>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </div>
  );
}

function MetaFields({
  title,
  setTitle,
  author,
  setAuthor,
  language,
  setLanguage,
  region,
  setRegion,
  titleRequired = true
}: {
  title: string;
  setTitle: (v: string) => void;
  author: string;
  setAuthor: (v: string) => void;
  language: string;
  setLanguage: (v: string) => void;
  region: string;
  setRegion: (v: string) => void;
  titleRequired?: boolean;
}) {
  return (
    <>
      <TextField label={titleRequired ? "Titel" : "Titel (optional, sonst aus Seite übernommen)"} value={title} onChange={setTitle} required={titleRequired} />
      <TextField label="Autor (optional)" value={author} onChange={setAuthor} />
      <div style={{ display: "flex", gap: "0.8rem" }}>
        <div style={{ flex: 1 }}>
          <TextField label="Sprache" value={language} onChange={setLanguage} required />
        </div>
        <div style={{ flex: 1 }}>
          <TextField label="Region (optional)" value={region} onChange={setRegion} />
        </div>
      </div>
    </>
  );
}

function NoteForm() {
  const { error, submitting, run } = useImportResult();
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [language, setLanguage] = useState("de");
  const [region, setRegion] = useState("");
  const [body, setBody] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileError(null);
    if (!/\.(txt|md)$/i.test(file.name)) {
      setFileError("Nur .txt- oder .md-Dateien werden unterstützt.");
      return;
    }
    try {
      const text = await file.text();
      setBody(text);
      if (!title) setTitle(file.name.replace(/\.(txt|md)$/i, ""));
    } catch {
      setFileError("Datei konnte nicht gelesen werden.");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await run(() =>
      api.post("/api/v1/sources/import/note", {
        title,
        author: author || undefined,
        language,
        region: region || undefined,
        body
      })
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      {error && <div className="pw-alert pw-alert--error">{error}</div>}
      {fileError && <div className="pw-alert pw-alert--error">{fileError}</div>}
      <MetaFields title={title} setTitle={setTitle} author={author} setAuthor={setAuthor} language={language} setLanguage={setLanguage} region={region} setRegion={setRegion} />
      <div className="pw-field">
        <label>Datei hochladen (.txt, .md — füllt den Text unten, optional)</label>
        <input type="file" accept=".txt,.md,text/plain,text/markdown" onChange={(e) => void onFileChange(e)} />
      </div>
      <div className="pw-field">
        <label>Text (Markdown, Absätze durch Leerzeile trennen)</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={10} style={{ padding: "0.65rem 0.8rem", borderRadius: "var(--pw-radius-sm)", border: "1px solid var(--pw-color-surface-border)", background: "var(--pw-color-surface)", color: "var(--pw-color-text)", fontFamily: "inherit", resize: "vertical" }} />
      </div>
      <button type="submit" className="pw-button pw-button--primary" disabled={submitting}>
        {submitting ? "Wird importiert …" : "Notiz importieren"}
      </button>
    </form>
  );
}

function ArticleForm() {
  const { error, submitting, run } = useImportResult();
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [language, setLanguage] = useState("de");
  const [region, setRegion] = useState("");
  const [url, setUrl] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await run(() =>
      api.post("/api/v1/sources/import/article-url", {
        title: title || undefined,
        author: author || undefined,
        language,
        region: region || undefined,
        url
      })
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      {error && <div className="pw-alert pw-alert--error">{error}</div>}
      <TextField label="URL" value={url} onChange={setUrl} required type="url" />
      <MetaFields title={title} setTitle={setTitle} author={author} setAuthor={setAuthor} language={language} setLanguage={setLanguage} region={region} setRegion={setRegion} titleRequired={false} />
      <button type="submit" className="pw-button pw-button--primary" disabled={submitting}>
        {submitting ? "Wird abgerufen …" : "Artikel importieren"}
      </button>
    </form>
  );
}

function TranscriptForm() {
  const { error, submitting, run } = useImportResult();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("de");
  const [region, setRegion] = useState("");
  const [author, setAuthor] = useState("");
  const [format, setFormat] = useState<"srt" | "vtt" | "txt" | "md">("srt");
  const [content, setContent] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await run(() => api.post("/api/v1/sources/import/transcript", { title, author: author || undefined, language, region: region || undefined, format, content }));
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      {error && <div className="pw-alert pw-alert--error">{error}</div>}
      <MetaFields title={title} setTitle={setTitle} author={author} setAuthor={setAuthor} language={language} setLanguage={setLanguage} region={region} setRegion={setRegion} />
      <div className="pw-field">
        <label>Format</label>
        <select value={format} onChange={(e) => setFormat(e.target.value as typeof format)} style={{ padding: "0.65rem 0.8rem", borderRadius: "var(--pw-radius-sm)", border: "1px solid var(--pw-color-surface-border)", background: "var(--pw-color-surface)", color: "var(--pw-color-text)" }}>
          <option value="srt">SRT (mit Zeitmarken)</option>
          <option value="vtt">VTT (mit Zeitmarken)</option>
          <option value="txt">TXT (nur Absätze)</option>
          <option value="md">Markdown (nur Absätze)</option>
        </select>
      </div>
      <div className="pw-field">
        <label>Inhalt</label>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} required rows={10} style={{ padding: "0.65rem 0.8rem", borderRadius: "var(--pw-radius-sm)", border: "1px solid var(--pw-color-surface-border)", background: "var(--pw-color-surface)", color: "var(--pw-color-text)", fontFamily: "var(--pw-font-mono)", fontSize: "0.85rem", resize: "vertical" }} />
      </div>
      <button type="submit" className="pw-button pw-button--primary" disabled={submitting}>
        {submitting ? "Wird verarbeitet …" : "Transkript importieren"}
      </button>
    </form>
  );
}

function PdfForm() {
  const { error, submitting, run } = useImportResult();
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [language, setLanguage] = useState("de");
  const [region, setRegion] = useState("");
  const [file, setFile] = useState<File | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    await run(() => {
      const form = new FormData();
      form.set("title", title);
      if (author) form.set("author", author);
      form.set("language", language);
      if (region) form.set("region", region);
      form.set("file", file);
      return api.postForm("/api/v1/sources/import/pdf", form);
    });
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      {error && <div className="pw-alert pw-alert--error">{error}</div>}
      <MetaFields title={title} setTitle={setTitle} author={author} setAuthor={setAuthor} language={language} setLanguage={setLanguage} region={region} setRegion={setRegion} />
      <div className="pw-field">
        <label>PDF-Datei</label>
        <input type="file" accept="application/pdf" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </div>
      <p style={{ fontSize: "0.78rem", color: "var(--pw-color-text-muted)", margin: "0 0 0.8rem" }}>
        Seitentext wird noch nicht automatisch extrahiert (folgt mit der Verarbeitungspipeline in Phase 3) — die Datei wird geprüft und gespeichert.
      </p>
      <button type="submit" className="pw-button pw-button--primary" disabled={submitting || !file}>
        {submitting ? "Wird hochgeladen …" : "PDF importieren"}
      </button>
    </form>
  );
}

export function ImportPage() {
  const [tab, setTab] = useState<Tab>("note");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.8rem", fontSize: "1.35rem", fontWeight: 600 }}>Import</h1>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className="pw-button"
              style={tab === t.id ? { background: "var(--pw-color-accent-bg-hover)", borderColor: "var(--pw-color-accent-border)" } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="pw-card">
        {tab === "note" && <NoteForm />}
        {tab === "article" && <ArticleForm />}
        {tab === "transcript" && <TranscriptForm />}
        {tab === "pdf" && <PdfForm />}
      </section>
    </div>
  );
}
